import { useEffect, useMemo, useRef, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useDispatch, useSelector } from 'react-redux';

import { type PartFileUnits } from '@frontend/api';
import { Box, useMediaQuery } from '@frontend/components';

import { GenericError } from 'modules/common/components/errorBoundary/GenericError';
import { useWebViewerContext } from 'modules/common/viewer/pdfViewer/WebViewerContext';
import {
  temporarilyHideSuggestionAnnotations,
  restoreSuggestionAnnotations,
} from 'modules/common/viewer/pdfViewer/annotationCommands/suggestionUtils';
import { useAnnotationOnSelectCallbacks } from 'modules/common/viewer/pdfViewer/annotationCommands/useAnnotationOnSelectCallbacks';
import { useSuggestionAnnotations } from 'modules/common/viewer/pdfViewer/annotationCommands/useSuggestionAnnotations';
import { applyRedactionsWithoutAttachments } from 'modules/common/viewer/pdfViewer/utils';
import { ReqsReviewConfigProvider } from 'modules/rules/ReqsReviewConfigContext';

import ApplyRedactionsModal from './ApplyRedactionsModal';
import { SuggestionAnnotationFlyout } from './SuggestionAnnotationFlyout';
import { useToggleShowExtractions } from './annotationCommands';
import { buildSuggestionAnnotationClass } from './annotationCommands/SuggestionAnnotation';
import { useSuggestionAnnotationFlyout } from './annotationCommands/useSuggestionAnnotationFlyout';
import { CalloutAuthor } from './calloutAuthor/CalloutAuthor';
import { useCalloutAuthor } from './calloutAuthor/useCalloutAuthor';
import { applyRedactions, pdfSearch } from './data/actions';
import { getRedactFileRequest, getSearchQuery } from './data/reducer';
import {
  useAnnotationEventListeners,
  useAnnotationInteractions,
  useCompareFilesCustomPanel,
  useCustomDownloadOptions,
  useCustomZoom,
  useInitialLeftPanelState,
  useInitializePDFViewer,
  useScaleMeasurements,
  useTextPopupButton,
  useUnitsPicker,
} from './hooks';
import { useManualTableSelection } from './hooks/useManualTableSelection';

type PDFViewerProps = {
  activeFileUuid: string | null;
  printUnits: PartFileUnits;
  onUpdatePrintUnits?: (units: PartFileUnits) => void;
  isPaused: boolean;
  isStandaloneViewer: boolean;
  isPartLibraryViewer: boolean;
  filename: string | null;
  url: string | null;
  isRequirementsReviewEnabled: boolean;
  isPdfRedactionEnabled: boolean;
  isBeamsEnabled: boolean;
  showAllExtractions?: boolean;
  toggleShowAllExtractions?: () => void;
};

// The actual PDF viewer content component
const PDFViewerContent = ({
  activeFileUuid,
  printUnits,
  onUpdatePrintUnits,
  isPaused,
  isStandaloneViewer,
  isPartLibraryViewer,
  filename,
  url,
  isRequirementsReviewEnabled,
  isPdfRedactionEnabled,
  isBeamsEnabled,
  showAllExtractions,
  toggleShowAllExtractions,
}: PDFViewerProps) => {
  const dispatch = useDispatch();
  const viewerRef = useRef<HTMLDivElement>(null);
  const [isLoadedByUrl, setIsLoadedByUrl] = useState<Record<string, boolean>>(
    {},
  );

  const [selectedAnnotationIds, setSelectedAnnotationIds] = useState<
    Set<string>
  >(new Set());

  const { instance, initializeWebViewer } = useWebViewerContext();

  // NB: This is the only way we can make custom PDFTron classes, by defining them
  // as an extension of the actual webViewerInstance.Core.Annotations.CustomAnnotation
  // class. That object does not exist until runtime.
  const SuggestionAnnotationClass = useMemo(() => {
    if (instance?.Core.annotationManager) {
      return buildSuggestionAnnotationClass({
        webViewerInstance: instance,
        annotationManager: instance.Core.annotationManager,
      });
    }
    return null;
  }, [instance]);

  // on mount initialize webviewer
  useEffect(() => {
    if (!instance && viewerRef.current) {
      initializeWebViewer(viewerRef.current);
    }
  }, [initializeWebViewer, instance]);

  // handle creating a new blob with the document's current redactions
  const redactFileRequest = useSelector(getRedactFileRequest);
  useEffect(() => {
    if (instance && redactFileRequest && url) {
      const annotationManager = instance.Core.annotationManager;

      // Temporarily remove SuggestionAnnotations before applying redactions
      const { stateMap, suggestionAnnotations } =
        temporarilyHideSuggestionAnnotations(annotationManager);

      annotationManager
        .applyRedactions()
        .then(async () => {
          const redactedBlob = await applyRedactionsWithoutAttachments({
            instance,
          });
          dispatch(applyRedactions.redactFile({ redactedBlob }));
          instance.UI.loadDocument(url);
        })
        .finally(() => {
          // Always restore SuggestionAnnotations after redactions are applied
          restoreSuggestionAnnotations(
            annotationManager,
            suggestionAnnotations,
            stateMap,
          );
        });
    }
  }, [instance, dispatch, redactFileRequest, url]);

  // handle applying search
  const searchQuery = useSelector(getSearchQuery);
  useEffect(() => {
    if (instance && searchQuery) {
      instance.UI.searchTextFull(searchQuery);
      dispatch(pdfSearch.success());
    }
  }, [dispatch, instance, searchQuery]);

  useScaleMeasurements({ instance });

  // BEGIN CUSTOM TOOLS - order here matters - keep it this way or instead combine all the hooks
  //  that build custom tools into a single hook for more granular control
  useManualTableSelection({ instance, isPartLibraryViewer, isBeamsEnabled });

  const { calloutAuthorInfo, handleCloseCalloutAuthor } = useCalloutAuthor({
    instance,
    url,
    isRequirementsReviewEnabled,
    isStandaloneViewer,
    isPartLibraryViewer,
  });

  useUnitsPicker({ instance, printUnits, onUpdatePrintUnits });

  useToggleShowExtractions({
    instance,
    isShowAllExtractionsChecked: showAllExtractions,
    onToggleShowAllExtractions: toggleShowAllExtractions,
  });
  // END CUSTOM TOOLS

  useAnnotationInteractions({
    isLoaded: isPaused ? false : isLoadedByUrl[url ?? ''] ?? false,
    instance,
    selectedAnnotationIds,
    isStandaloneViewer,
  });

  useSuggestionAnnotations({
    isLoaded: isPaused ? false : isLoadedByUrl[url ?? ''] ?? false,
    annotationManager: instance?.Core.annotationManager ?? null,
    documentViewer: instance?.Core.documentViewer ?? null,
    webViewerInstance: instance,
    activeFileUuid,
    SuggestionAnnotationClass,
  });

  useInitializePDFViewer({
    instance,
    isStandaloneViewer,
    setIsLoadedByUrl,
    SuggestionAnnotationClass,
    isPdfRedactionEnabled,
  });

  useTextPopupButton({
    instance,
    isPartLibraryViewer,
    isStandaloneViewer,
    isRequirementsReviewEnabled,
  });

  useAnnotationEventListeners({
    instance,
    selectedAnnotationIds,
    setSelectedAnnotationIds,
  });

  useCustomDownloadOptions({
    instance,
    filename,
    isLoaded: isPaused ? false : isLoadedByUrl[url ?? ''] ?? false,
  });

  // setting up compare custom panel
  useCompareFilesCustomPanel({
    instance,
    filename,
    url,
  });

  useInitialLeftPanelState({ instance });

  useCustomZoom({
    instance,
    isLoaded: isPaused ? false : isLoadedByUrl[url ?? ''] ?? false,
  });

  // now loading in the document
  const shouldLoadDocumentUrl = useMemo(() => {
    return Boolean(url && !isLoadedByUrl[url]);
  }, [isLoadedByUrl, url]);

  useEffect(() => {
    if (instance && !isPaused && url && shouldLoadDocumentUrl) {
      const loadDocByUrl = async () => {
        instance.UI.loadDocument(url, { documentId: url });
      };

      loadDocByUrl();
    }
  }, [instance, isPaused, shouldLoadDocumentUrl, url]);

  useAnnotationOnSelectCallbacks({
    instance,
    annotationManager: instance?.Core.annotationManager ?? null,
    SuggestionAnnotationClass,
  });

  const { flyoutState, closeFlyout } = useSuggestionAnnotationFlyout({
    instance,
    SuggestionAnnotationClass,
    isStandaloneViewer,
    isPartLibraryViewer,
  });

  const [isMobile] = useMediaQuery('(max-width: 992px)');

  return (
    <Box
      height={isMobile ? 'calc(100% - 50px)' : '100%'}
      marginTop={isMobile ? '50px' : 'unset'}
      visibility={isPaused ? 'hidden' : 'unset'}
      position="relative"
    >
      <ErrorBoundary
        fallback={
          <GenericError size="lg" height="100%" justifyContent="center" />
        }
        onError={(error) =>
          console.error('Error occurred in PDFViewerContent', error)
        }
      >
        <Box height="100%" className="pdf-viewer-container" ref={viewerRef} />
      </ErrorBoundary>
      <ApplyRedactionsModal />
      <CalloutAuthor
        activeFileUuid={activeFileUuid}
        calloutAuthorInfo={calloutAuthorInfo}
        handleCloseCalloutAuthor={handleCloseCalloutAuthor}
      />
      {!isStandaloneViewer && !isPartLibraryViewer && (
        <SuggestionAnnotationFlyout
          instance={instance}
          isOpen={flyoutState.isOpen}
          virtualReference={flyoutState.virtualReference}
          annotation={flyoutState.annotation}
          onClose={closeFlyout}
        />
      )}
    </Box>
  );
};

export const PDFViewer = (props: PDFViewerProps) => {
  return (
    <ReqsReviewConfigProvider>
      <PDFViewerContent {...props} />
    </ReqsReviewConfigProvider>
  );
};
